import { useRef } from "react";
import {
	useOverlay,
	usePreventScroll,
	useModal,
	OverlayContainer,
} from "@react-aria/overlays";
import { useDialog } from "@react-aria/dialog";
import { FocusScope } from "@react-aria/focus";

import useViewport from "../../../hooks/useViewport";
import { ModalProps } from "./Modal.types";
import {
	StyledContentContainer,
	StyledModalContainer,
	StyledModalDialogContainer,
	StyledModalScrollContainer,
	StyledOverlay,
} from "./styling/Modal.styles";
import PageSection from "../../layout/PageSection";
import ModalContent from "./ModalContent";

const Modal = ({
	onClose,
	isOpen,
	children,
	shouldCloseOnBlur = false,
	role = "dialog",
	id,
	"aria-label": ariaLabel,
	"aria-labelledby": ariaLabelledby,
	"aria-describedby": ariaDescribedby,
	"aria-details": ariaDetails,
	...props
}: ModalProps) => {
	const ref = useRef(null);

	const { overlayProps } = useOverlay(
		{
			isOpen,
			onClose,
			shouldCloseOnBlur,
		},
		ref
	);

	usePreventScroll();

	const modalProps = useModal();
	const { dialogProps } = useDialog(
		{
			role,
			id,
			"aria-label": ariaLabel,
			"aria-labelledby": ariaLabelledby,
			"aria-describedby": ariaDescribedby,
			"aria-details": ariaDetails,
		},
		ref
	);

	const viewport = useViewport();
	const isMobile = viewport.lessThan.sm;

	return isOpen ? (
		<OverlayContainer>
			<FocusScope autoFocus contain restoreFocus>
				<StyledModalContainer>
					<StyledOverlay width="100vw" height="100vh" />
					<StyledModalScrollContainer viewport={viewport}>
						<StyledModalDialogContainer viewport={viewport}>
							<StyledContentContainer
								ref={ref}
								viewport={viewport}
								{...props}
								{...dialogProps}
								{...modalProps}
								{...overlayProps}
								tabIndex={1}
							>
								{isMobile ? (
									<PageSection>
										<ModalContent onClose={onClose}>{children}</ModalContent>
									</PageSection>
								) : (
									<ModalContent onClose={onClose}>{children}</ModalContent>
								)}
							</StyledContentContainer>
						</StyledModalDialogContainer>
					</StyledModalScrollContainer>
				</StyledModalContainer>
			</FocusScope>
		</OverlayContainer>
	) : null;
};

export default Modal;
